import { useState } from "react";
import { toast } from "sonner";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Sparkle, PencilSimple, Plus, Trash, Check, X } from "@phosphor-icons/react";
import { SectionText, SectionTable } from "./hseUtils";

/** One typed section (text or table) with inline editing and a per-section AI re-draft. */
export function HseSectionEditor({ docId, index, section, value, canEdit, aiBusy, onSaved, onRedraftStarted }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [brief, setBrief] = useState("");
  const isTable = section.kind === "table";
  const start = () => { setDraft(isTable ? (Array.isArray(value) ? value.map((r) => ({ ...r })) : []) : (value || "")); setEditing(true); };
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await API.put(`/hse/documents/${docId}`, { sections: { [section.key]: draft } });
      onSaved(data); setEditing(false); toast.success("Section saved");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't save"); } finally { setSaving(false); }
  };
  const redraft = async () => {
    try {
      const { data } = await API.post(`/hse/documents/${docId}/ai-redraft`, { section: section.key, brief });
      toast.success(data.message); setAiOpen(false); setBrief(""); onRedraftStarted(data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't start the re-draft"); }
  };
  const setCell = (i, c, v) => setDraft((rows) => rows.map((r, k) => (k === i ? { ...r, [c]: v } : r)));
  return (
    <section className="rounded-lg border border-border bg-card p-4" data-testid={`hse-section-${section.key}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <h3 className="text-sm font-semibold">{index}. {section.title}</h3>
          {section.hint && <p className="text-[11px] text-muted-foreground">{section.hint}</p>}
        </div>
        {canEdit && !editing && (
          <div className="flex gap-1 shrink-0">
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setAiOpen(true)} disabled={aiBusy} data-testid={`hse-section-ai-${section.key}`}><Sparkle size={13} className="mr-1" /> Re-draft</Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={start} disabled={aiBusy} data-testid={`hse-section-edit-${section.key}`}><PencilSimple size={13} className="mr-1" /> Edit</Button>
          </div>
        )}
      </div>
      {editing ? (
        <div className="space-y-2">
          {isTable ? (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr>{section.columns.map((c) => <th key={c} className="text-left px-1 py-1 font-semibold capitalize">{c.replace(/_/g, " ")}</th>)}<th /></tr></thead>
                <tbody>
                  {draft.map((r, i) => (
                    <tr key={i}>
                      {section.columns.map((c) => <td key={c} className="px-1 py-0.5 align-top"><Textarea rows={2} value={r[c] || ""} onChange={(e) => setCell(i, c, e.target.value)} className="text-xs min-h-[40px]" data-testid={`hse-cell-${section.key}-${i}-${c}`} /></td>)}
                      <td className="px-1 align-top"><button type="button" onClick={() => setDraft((rows) => rows.filter((_, k) => k !== i))} className="text-muted-foreground hover:text-red-600 mt-2" data-testid={`hse-row-remove-${section.key}-${i}`}><Trash size={14} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Button size="sm" variant="outline" className="h-7 text-xs mt-1" onClick={() => setDraft((rows) => [...rows, Object.fromEntries(section.columns.map((c) => [c, ""]))])} data-testid={`hse-row-add-${section.key}`}><Plus size={12} className="mr-1" /> Add row</Button>
            </div>
          ) : (
            <Textarea rows={Math.min(24, Math.max(6, (draft || "").split("\n").length + 2))} value={draft} onChange={(e) => setDraft(e.target.value)} className="text-sm font-mono" data-testid={`hse-section-textarea-${section.key}`} />
          )}
          <p className="text-[11px] text-muted-foreground">Plain text: a line ending with ":" is a sub-heading, "- " starts a bullet, "1. " a numbered step.</p>
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)} disabled={saving}><X size={13} className="mr-1" /> Cancel</Button>
            <Button size="sm" onClick={save} disabled={saving} data-testid={`hse-section-save-${section.key}`}><Check size={13} className="mr-1" /> {saving ? "Saving…" : "Save section"}</Button>
          </div>
        </div>
      ) : isTable ? <SectionTable rows={Array.isArray(value) ? value : []} columns={section.columns} /> : <SectionText text={value} />}

      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="max-w-lg" data-testid="hse-section-ai-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkle size={16} weight="fill" className="text-primary" /> Re-draft "{section.title}" with AI</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Only this section is rewritten. Tell the AI what to change or add — or leave blank for a fresh take.</p>
          <Input value={brief} onChange={(e) => setBrief(e.target.value)} placeholder="e.g. mention our two-person rule for roof access" data-testid="hse-section-ai-brief" />
          <DialogFooter><Button variant="outline" onClick={() => setAiOpen(false)}>Cancel</Button><Button onClick={redraft} data-testid="hse-section-ai-go"><Sparkle size={13} className="mr-1" /> Re-draft section</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
